/**
 * One assistant answer as the panel renders it (#765, spec §8.1–8.2): the
 * direct answer, the theses with their citations, the sources line and the
 * suggested follow-ups. Text is plain — the model is told "no markdown"
 * (§3.2 п.6) and the structure comes from the schema, so `whitespace-pre-line`
 * is all the formatting there is.
 *
 * A turn can carry an error code with EITHER status: `failed` for a turn
 * that never produced an answer, and `completed` when the answer exists but
 * cannot be shown — `access_revoked` (a cited record is no longer visible,
 * §5.2) or `answer_invalid` (the stored output does not pass the read-side
 * guard). Both render the translated reason, never an empty bubble
 * (Codex P2, round 1).
 */

import { useTranslation } from 'react-i18next';
import type {
  AssistantScope,
  AssistantAnswer as AssistantAnswerDto,
  AssistantCitation,
} from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { assistantErrorKeys } from '@/lib/assistant/error-keys';
import { QuoteButton } from './QuoteButton';

/**
 * The wording keys per scope — one table, not a chain of booleans per string
 * (#1056 review). Only the whole base and a deal's feed lose records to the
 * redaction (`unreadable`); a single record fails as a whole instead.
 */
const SCOPE_KEYS = {
  record: {
    notFound: 'answer.notFound',
    truncated: 'answer.truncated',
    unreadable: null,
  },
  entity: {
    notFound: 'answer.notFoundEntity',
    truncated: 'answer.truncatedEntity',
    unreadable: {
      notFound: 'answer.notFoundEntityUnreadable',
      sources: 'answer.unreadableEntity',
    },
  },
  org: {
    notFound: 'answer.notFoundOrg',
    truncated: 'answer.truncatedOrg',
    unreadable: {
      notFound: 'answer.notFoundOrgUnreadable',
      sources: 'answer.unreadableOrg',
    },
  },
} as const satisfies Record<AssistantScope, unknown>;

export interface AssistantAnswerProps {
  answer: AssistantAnswerDto;
  onQuote: (citation: AssistantCitation) => void;
  onSuggested: (question: string) => void;
  /** No question can be sent right now — one is in flight, or the daily limit is spent. */
  suggestionsDisabled?: boolean;
  /**
   * The topic's scope, not a flag derived from it: refusal wording is looked
   * up by scope, and a boolean would send every future scope to the record
   * wording (#1056 review). About a deal (#765 Ф2) «nothing found» speaks of
   * the conversations, and a quote from a record other than
   * `onScreenRecordId` says so; about the whole base (Ф3) the line also says
   * how many records the search chose from.
   */
  scope?: AssistantScope;
  /** The record open on screen, or `null`. */
  onScreenRecordId?: string | null;
  /**
   * The verdict arrived while the panel was open — announce it. History is
   * read, not announced: `role="alert"` on every old failure read them all
   * aloud each time the panel opened (owner's review, 17.09).
   */
  live?: boolean;
  /**
   * The question was asked under whole-base filters (shown above the answer).
   * With no record to search, the answer then points at them, not at the base.
   */
  filtered?: boolean;
}

export function AssistantAnswer({
  answer,
  onQuote,
  onSuggested,
  suggestionsDisabled = false,
  scope = 'record',
  onScreenRecordId = null,
  live = false,
  filtered = false,
}: AssistantAnswerProps) {
  const { t } = useTranslation('assistant');
  const aboutEntity = scope === 'entity';
  const aboutOrg = scope === 'org';
  // Nothing to search at all is not «nothing about this»: the filters, or the
  // base itself, left no record — and the user has to know which, or a
  // forgotten category reads as a broken customer filter (#1361).
  const noRecords = aboutOrg && answer.sources.recordsConsidered === 0;
  const keys = SCOPE_KEYS[scope];
  const notFoundKey = noRecords
    ? filtered
      ? 'answer.noRecordsUnderFilters'
      : 'answer.noRecordsOrg'
    : keys.notFound;
  // A negative about «the conversations the search found» is false when some
  // of them could not be read (#2427, a deal's feed #2473): the wording says
  // it was the read ones.
  const unreadableKeys =
    (answer.sources.recordsUnreadable ?? 0) > 0 ? keys.unreadable : null;
  const notFoundText = t(unreadableKeys ? unreadableKeys.notFound : notFoundKey);
  const truncatedKey = keys.truncated;
  // How many records the search could choose from — only the whole base
  // chooses; a record or a deal answers from what it is.

  if (answer.status === 'failed' || answer.errorCode) {
    const code = answer.errorCode ?? 'generic';
    return (
      <p
        className="text-sm text-fg-muted"
        role={live ? 'alert' : undefined}
        data-testid="assistant-answer-error"
      >
        {t(assistantErrorKeys(code, scope))}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {answer.answer && (
        <p className="text-sm text-fg whitespace-pre-line">{answer.answer}</p>
      )}
      {answer.notFound && <p className="text-sm text-fg-muted">{notFoundText}</p>}

      {answer.points.length > 0 && (
        <ol className="flex flex-col gap-3">
          {answer.points.map((point, index) => (
            <li
              key={`${answer.turnId}:${index}`}
              className="rounded-card border border-border bg-bg-elevated p-3"
            >
              <p className="text-sm font-medium text-fg">{point.thesis}</p>
              {point.detail && (
                <p className="mt-1 text-sm text-fg-muted whitespace-pre-line">
                  {point.detail}
                </p>
              )}
              {point.citations.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  {point.citations.map((citation, citationIndex) => (
                    <QuoteButton
                      key={`${citation.recordId}:${citation.startSec ?? 'x'}:${citationIndex}`}
                      citation={citation}
                      onPlay={onQuote}
                      otherRecord={
                        (aboutEntity || aboutOrg) &&
                        citation.recordId !== onScreenRecordId
                      }
                    />
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {/* Truncation is said even with nothing cited: a long record cut at the
          context cap answers "not found" with `recordsCited: 0` and
          `truncated: true` — the user must know only part was read (Codex
          P2, round 10). A whole-base answer always says how many records it
          searched — zero most of all: it tells filters that matched nothing
          from a search that read records and found no answer (Codex P2, #1312). */}
      {(aboutOrg ||
        unreadableKeys ||
        answer.sources.recordsCited > 0 ||
        answer.sources.truncated) && (
        <p className="text-xs text-fg-subtle" data-testid="assistant-sources">
          {[
            aboutOrg &&
              t('answer.consideredOrg', { count: answer.sources.recordsConsidered }),
            answer.sources.recordsCited > 0 &&
              t('answer.sources', { count: answer.sources.recordsCited }),
            answer.sources.truncated && t(truncatedKey),
            unreadableKeys &&
              t(unreadableKeys.sources, {
                count: answer.sources.recordsUnreadable,
              }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}

      {answer.suggestedQuestions.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-fg-muted">{t('answer.suggested')}</p>
          <div className="flex flex-wrap gap-2">
            {answer.suggestedQuestions.map((question, index) => (
              <Button
                key={`${answer.turnId}:${index}`}
                variant="outline"
                size="sm"
                disabled={suggestionsDisabled}
                onClick={() => onSuggested(question)}
              >
                {question}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
